// CourseDetailPage — Single course view
// Layout: hero w/ cover, progress, lessons list

import { useNavigate, useParams } from 'react-router-dom';

interface Course {
  id: string;
  title: string;
  description: string;
  cover: string;
  color: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  category: string;
  duration: string;
  rating: number;
  enrolled: number;
  instructor: string;
  instructorRole: string;
  progress: number;
  lessons: { title: string; duration: string; completed: boolean; locked?: boolean }[];
  outcomes: string[];
}

const COURSES: Record<string, Course> = {
  'c1': {
    id: 'c1',
    title: 'Prop Firm Trading Fundamentals',
    description: 'How funded accounts work, drawdown rules, and your first challenge',
    cover: '🎓', color: '#34C759', level: 'Beginner', category: 'Beginner',
    duration: '2h 30m', rating: 4.9, enrolled: 8420,
    instructor: 'Sarah Chen', instructorRole: 'Senior Trader · 8 yrs',
    progress: 100,
    lessons: [
      { title: 'What is a prop firm?', duration: '8m',  completed: true },
      { title: 'How funded accounts work', duration: '12m', completed: true },
      { title: 'Understanding drawdown', duration: '15m', completed: true },
      { title: 'EOD balance vs trailing', duration: '10m', completed: true },
      { title: 'Profit split explained', duration: '8m',  completed: true },
      { title: 'Choosing your tier', duration: '12m', completed: true },
      { title: 'Platform basics', duration: '14m', completed: true },
      { title: 'Setting up your bot', duration: '10m', completed: true },
      { title: 'Risk management 101', duration: '15m', completed: true },
      { title: 'Your first 10 trades', duration: '12m', completed: true },
      { title: 'Tracking your progress', duration: '8m',  completed: true },
      { title: 'Final assessment', duration: '6m',  completed: true },
    ],
    outcomes: [
      'Understand how prop firms evaluate traders',
      'Choose the right account size and platform',
      'Manage risk to pass your first challenge',
      'Set up your AI bot for consistent execution',
    ],
  },
  'c2': {
    id: 'c2',
    title: 'Reading the DOM Like a Pro',
    description: 'Order flow, iceberg orders, and reading the depth of market',
    cover: '📊', color: '#007AFF', level: 'Intermediate', category: 'Trading',
    duration: '3h 15m', rating: 4.8, enrolled: 5340,
    instructor: 'Marcus Kim', instructorRole: 'Prop Trader · 6 yrs',
    progress: 39,
    lessons: [
      { title: 'Introduction to order flow', duration: '10m', completed: true },
      { title: 'DOM structure and layout', duration: '12m', completed: true },
      { title: 'Bid/ask dynamics', duration: '15m', completed: true },
      { title: 'Iceberg orders', duration: '12m', completed: true },
      { title: 'Sweeps and raids', duration: '14m', completed: true },
      { title: 'Volume at price', duration: '15m', completed: true },
      { title: 'Cumulative delta', duration: '12m', completed: true },
      { title: 'Absorption vs exhaustion', duration: '14m', completed: false },
      { title: 'Stack imbalances', duration: '10m', completed: false },
      { title: 'Stop runs and traps', duration: '12m', completed: false },
      { title: 'Multi-timeframe order flow', duration: '15m', completed: false },
      { title: 'Live trade examples', duration: '20m', completed: false },
      { title: 'Building a DOM strategy', duration: '18m', completed: false },
      { title: 'Backtesting order flow', duration: '12m', completed: false },
      { title: 'Risk management', duration: '10m', completed: false },
      { title: 'Mental game', duration: '8m',  completed: false },
      { title: 'Common mistakes', duration: '10m', completed: false },
      { title: 'Final exam', duration: '6m',  completed: false },
    ],
    outcomes: [
      'Read DOM signals with confidence',
      'Detect institutional activity',
      'Trade with order flow confluence',
      'Build your own DOM strategy',
    ],
  },
};

export function CourseDetailPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const c = COURSES[id || 'c1'] || COURSES.c1;
  const completedLessons = c.lessons.filter((l) => l.completed).length;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">{c.category}</div>
        <button onClick={() => alert('Share')} className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center" style={{ background: `linear-gradient(135deg, ${c.color} 0%, ${c.color}AA 100%)`, color: 'white' }}>
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] text-[44px]" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(20px)' }}>{c.cover}</div>
          <div className="mt-3 inline-flex items-center gap-1.5">
            <span className="rounded-full bg-white/20 backdrop-blur-md px-2 py-0.5 text-[10px] font-bold uppercase">{c.level}</span>
            <span className="text-[12px]">★ {c.rating} · {c.enrolled.toLocaleString()} students</span>
          </div>
          <div className="mt-2 text-[20px] font-bold tracking-tight">{c.title}</div>
          <p className="mt-1 text-[12px] max-w-xs mx-auto" style={{ color: 'rgba(255,255,255,0.9)' }}>{c.description}</p>
          <div className="mt-3 text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>
            {c.instructor} · {c.instructorRole} · {c.duration}
          </div>
        </div>
      </div>

      {/* Progress */}
      {c.progress > 0 && (
        <div className="mx-4 mt-3 ios26-card p-4">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Your progress</div>
            <div className="text-[12px] font-semibold tabular-nums" style={{ color: c.color }}>{c.progress}%</div>
          </div>
          <div className="mt-1 text-[18px] font-bold text-tf-text-1">{completedLessons} of {c.lessons.length} lessons</div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-tf-surface-1">
            <div className="h-full rounded-full transition-all" style={{ width: c.progress + '%', backgroundColor: c.color }} />
          </div>
          {c.progress < 100 && (
            <button onClick={() => alert('Resume lesson')} data-testid="resume-course" className="mt-3 flex h-11 w-full items-center justify-center rounded-full text-[14px] font-semibold text-white active:opacity-40" style={{ backgroundColor: c.color }}>
              {c.progress === 0 ? 'Start course' : 'Continue learning'}
            </button>
          )}
        </div>
      )}

      {/* Outcomes */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">What you'll learn</h2>
      <div className="mx-4 ios26-list bg-white">
        {c.outcomes.map((o, i) => (
          <div key={i} className="flex items-start gap-3 border-b border-tf-border last:border-b-0 px-4 py-2.5" data-testid={`outcome-${i}`}>
            <div className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#34C759] text-white">
              <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <div className="text-[13px] text-tf-text-1">{o}</div>
          </div>
        ))}
      </div>

      {/* Lessons */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Lessons ({c.lessons.length})</h2>
      <div className="mx-4 ios26-list bg-white">
        {c.lessons.map((l, i) => (
          <button
            key={i}
            onClick={() => !l.locked && alert('Play: ' + l.title)}
            disabled={l.locked}
            data-testid={`lesson-${i}`}
            className={['flex w-full items-center gap-3 px-4 py-3 transition',
              i < c.lessons.length - 1 ? 'border-b border-tf-border' : '',
              l.locked ? 'opacity-50' : 'active:bg-tf-surface-1'
            ].join(' ')}
          >
            <div className={['flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold',
              l.completed ? 'bg-[#34C759] text-white' : 'bg-tf-surface-1 text-tf-text-3'
            ].join(' ')}>
              {l.completed ? '✓' : l.locked ? '🔒' : i + 1}
            </div>
            <div className="flex-1 text-left">
              <div className="text-[14px] font-semibold text-tf-text-1">{l.title}</div>
              <div className="text-[11px] text-tf-text-3">⏱ {l.duration}</div>
            </div>
            {!l.completed && !l.locked && (
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-blue" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
